// hooks/useSearch.js
import { datasets } from "../../constants/mapConstants";
import { IDatasetType } from "../../models/map.interface";
import { useActions } from "../useActions";
import { useTypedSelector } from "../useTypedSelector";
import { ISuggestion } from "../../models/Search";

export const useActionsByDataset = (
  dataset: IDatasetType = datasets.objects
) => {
  const datasetFiltersNames = {
    [datasets.objects]: "mapFilters",
    [datasets.company]: "companyFilters",
    [datasets.person]: "personFilters",
    [datasets.sika]: "sikaDesignerFilters",
    [datasets.usaid]: "usaidMapFilters",
    [datasets.cybersecurity]: "cybersecurityMapFilters",
  };
  const selectedDatasetFilterName = datasetFiltersNames[dataset];
  const selectedDatasetFilters = useTypedSelector(
    (state) => state[selectedDatasetFilterName]
  );
  const { activeSuggestions, suggestions } = selectedDatasetFilters;

  //main
  const {
    getSuggestions,
    setSuggestions,
    setActiveSuggestions,
    addActiveSuggestion,
    deleteActiveSuggestion,
    deleteRegion,
    deleteEdrCode,
    deleteNpId,
    deleteKeyword,
    deleteDkbsCode,
  } = useActions();

  //companyActions
  const {
    getCompanySuggestions,
    setCompanySuggestions,
    setCompanyActiveSuggestions,
    deleteCompanyActiveSuggestion,
    deleteCompanyKVED,
    deleteCompanyRegion,
    addCompanyActiveSuggestion,
    deleteCompanyEdrCode,
  } = useActions();

  //personActions

  const {
    getPersonSuggestions,
    setPersonSuggestions,
    setPersonActiveSuggestions,
    deletePersonActiveSuggestion,
    deletePersonKVED,
    deletePersonRegion,
    deletePersonHashCode,
    addPersonsActiveSuggestion,
  } = useActions();

  //sika
  const {
    getSikaDesignerSuggestions,
    setSikaDesignerSuggestions,
    setSikaDesignerActiveSuggestions,
    deleteSikaDesignerActiveSuggestion,
    deleteSikaDesignerKVED,
    deleteSikaDesignerRegion,
    deleteSikaDesignerEdrCode,
    deleteSikaDesignerKeyword,
    addSikaActiveSuggestion,
  } = useActions();
  //usaid
  const {
    getUSAIDSuggestions,
    setUSAIDSuggestions,
    setUSAIDActiveSuggestions,
    deleteUSAIDActiveSuggestion,
    deleteUSAIDKVED,
    deleteUSAIDRegion,
    deleteUSAIDEdrCode,
    addUSaidActiveSuggestion,
  } = useActions();
  //cybersecurity
  const {
    getCybersecuritySuggestions,
    setCybersecuritySuggestions,
    setCybersecurityActiveSuggestions,
    deleteCybersecurityActiveSuggestion,
    addCybersecurityActiveSuggestion,
  } = useActions();

  const handleDeleteActiveSuggestionsForMain = (suggestion: ISuggestion) => {
    deleteActiveSuggestion(suggestion);
    deleteRegion(suggestion.code);
    deleteEdrCode(suggestion.code);
    deleteNpId(suggestion.code);
    deleteKeyword(suggestion.code);
    deleteDkbsCode(suggestion.code);
  };

  const handleDeleteActiveSuggestionsForCompany = (suggestion: ISuggestion) => {
    deleteCompanyActiveSuggestion(suggestion);
    deleteCompanyRegion(suggestion.code);
    deleteCompanyEdrCode(suggestion.code);
    deleteCompanyKVED(suggestion.code);
    deleteKeyword(suggestion.code);
  };

  const handleDeleteActiveSuggestionsForPersons = (suggestion: ISuggestion) => {
    deletePersonActiveSuggestion(suggestion);
    deletePersonRegion(suggestion.code);
    deletePersonHashCode(suggestion.code);
    deletePersonKVED(suggestion.code);
    deleteKeyword(suggestion.code);
  };

  const handleDeleteActiveSuggestionsForSika = (suggestion: ISuggestion) => {
    deleteSikaDesignerActiveSuggestion(suggestion);
    deleteSikaDesignerRegion(suggestion.code);
    deleteSikaDesignerEdrCode(suggestion.code);
    deleteSikaDesignerKVED(suggestion.code);
    deleteSikaDesignerKeyword(suggestion.code);
    deleteKeyword(suggestion.code);
  };

  const handleDeleteActiveSuggestionsForUSaid = (suggestion: ISuggestion) => {
    deleteUSAIDActiveSuggestion(suggestion);
    deleteUSAIDRegion(suggestion.code);
    deleteUSAIDEdrCode(suggestion.code);
    deleteUSAIDKVED(suggestion.code);
    deleteKeyword(suggestion.code);
  };

  const handleDeleteActiveSuggestionsForCybersecurity = (
    suggestion: ISuggestion
  ) => {
    deleteCybersecurityActiveSuggestion(suggestion);
    deleteKeyword(suggestion.code);
  };

  const config: Record<string, any> = {
    [datasets.objects]: {
      getSuggestions,
      setSuggestions,
      setActiveSuggestions,
      handleDeleteActiveSuggestion: handleDeleteActiveSuggestionsForMain,
      addActiveSuggestion: addActiveSuggestion,
    },
    [datasets.company]: {
      getSuggestions: getCompanySuggestions,
      setSuggestions: setCompanySuggestions,
      setActiveSuggestions: setCompanyActiveSuggestions,
      handleDeleteActiveSuggestion: handleDeleteActiveSuggestionsForCompany,
      addActiveSuggestion: addCompanyActiveSuggestion,
    },
    [datasets.person]: {
      getSuggestions: getPersonSuggestions,
      setSuggestions: setPersonSuggestions,
      setActiveSuggestions: setPersonActiveSuggestions,
      handleDeleteActiveSuggestion: handleDeleteActiveSuggestionsForPersons,
      addActiveSuggestion: addPersonsActiveSuggestion,
    },

    [datasets.sika]: {
      getSuggestions: getSikaDesignerSuggestions,
      setSuggestions: setSikaDesignerSuggestions,
      setActiveSuggestions: setSikaDesignerActiveSuggestions,
      handleDeleteActiveSuggestion: handleDeleteActiveSuggestionsForSika,
      addActiveSuggestion: addSikaActiveSuggestion,
    },
    [datasets.usaid]: {
      getSuggestions: getUSAIDSuggestions,
      setSuggestions: setUSAIDSuggestions,
      setActiveSuggestions: setUSAIDActiveSuggestions,
      handleDeleteActiveSuggestion: handleDeleteActiveSuggestionsForUSaid,
      addActiveSuggestion: addUSaidActiveSuggestion,
    },
    [datasets.cybersecurity]: {
      getSuggestions: getCybersecuritySuggestions,
      setSuggestions: setCybersecuritySuggestions,
      setActiveSuggestions: setCybersecurityActiveSuggestions,
      handleDeleteActiveSuggestion:
        handleDeleteActiveSuggestionsForCybersecurity,
      addActiveSuggestion: addCybersecurityActiveSuggestion,
    },
  };

  return {
    getSuggestions: config[dataset].getSuggestions,
    setSuggestions: config[dataset].setSuggestions,
    setActiveSuggestions: config[dataset].setActiveSuggestions,
    deleteActiveSuggestion: config[dataset].deleteActiveSuggestion,
    handleDeleteActiveSuggestion: config[dataset].handleDeleteActiveSuggestion,
    addActiveSuggestion: config[dataset].addActiveSuggestion,
    activeSuggestions,
    suggestions,
  };
};

export default useActionsByDataset;
